"use client";

import React, { useState, useEffect, useRef } from "react";
import { Loader, Activity } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { cn } from "@/lib/utils";
interface WaveformProps {
  intensity: number;
}
const WaveformBackground = ({
  intensity
}: WaveformProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameCountRef = useRef(0);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    const draw = () => {
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const baseAmp = 50;
      const ampBoost = Math.map(intensity, 0, 1, 0, 100);
      const amplitude = baseAmp + ampBoost;
      const speed = Math.map(intensity, 0, 1, 0.02, 0.1);
      const t = frameCountRef.current * speed;
      ctx.strokeStyle = `rgba(255, 255, 255, ${0.8 + intensity})`;
      ctx.lineWidth = 1 + intensity * 2;

      // Draw multiple waveforms
      for (let i = 0; i < 5; i++) {
        ctx.beginPath();
        for (let x = 0; x <= canvas.width; x += 20) {
          const y = canvas.height / 2 + Math.sin(x * 0.01 + t + i * 0.5) * amplitude * 0.5 + Math.sin(x * 0.02 - t * 2) * amplitude * 0.3;
          const noiseValue = (Math.sin(x * 0.01 + t) - 0.5) * intensity * 100;
          if (x === 0) {
            ctx.moveTo(x, y + noiseValue);
          } else {
            ctx.lineTo(x, y + noiseValue);
          }
        }
        ctx.stroke();
      }
      frameCountRef.current++;
      requestAnimationFrame(draw);
    };
    const animationId = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [intensity]);

  // Helper function to map values (replaces p5.map)
  if (typeof Math.map === 'undefined') {
    Math.map = function (value: number, start1: number, stop1: number, start2: number, stop2: number) {
      return (value - start1) / (stop1 - start1) * (stop2 - start2) + start2;
    } as any;
  }
  return <canvas ref={canvasRef} className="absolute inset-0 z-0 opacity-20 pointer-events-none" data-section-id="6247" />;
};
function IgniteForm({
  onTyping
}: {
  onTyping: () => void;
}) {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="w-full max-w-md mx-auto p-8 text-center bg-white/10 backdrop-blur-md border border-white/20 rounded-sm animate-in fade-in zoom-in duration-500">
        <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-white/20 text-white">
          <Activity className="w-8 h-8" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-2">Offer Request Received</h3>
        <p className="text-white/80">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6 w-full max-w-2xl mx-auto backdrop-blur-sm bg-black/10 p-8 rounded-sm border border-white/20 shadow-2xl" onKeyDown={onTyping}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white font-medium">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" {...field} className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-white/50 transition-all duration-300 rounded-sm" />
              </FormControl>
              <FormMessage className="text-white/90" />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white font-medium">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-white/50 transition-all duration-300 rounded-sm" />
              </FormControl>
              <FormMessage className="text-white/90" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white font-medium">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 000-0000" {...field} className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-white/50 transition-all duration-300 rounded-sm" />
              </FormControl>
              <FormMessage className="text-white/90" />
            </FormItem>} />

        {}
        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white font-medium">Property Address</FormLabel>
              <FormControl>
                <Input placeholder="123 Main St, City, State" {...field} className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-white/50 transition-all duration-300 rounded-sm" />
              </FormControl>
              <FormMessage className="text-white/90" />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="growth_goal" rules={{
      required: "Property details are required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-white font-medium">Property Condition & Selling Timeline</FormLabel>
            <FormControl>
              <textarea {...field} placeholder="Briefly describe the condition and how soon you need to close..." className={cn("flex min-h-[120px] w-full rounded-sm border border-white/20 bg-white/10 px-3 py-2 text-sm text-white placeholder:text-white/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-300 resize-y", "focus:border-white/50")} />
            </FormControl>
            <FormMessage className="text-white/90" />
          </FormItem>} />

      {submitError && <div className="p-3 bg-white/20 border border-white/30 rounded-sm text-white text-sm">
          {submitError}
        </div>}

      <div className="pt-4 flex justify-center">
        <Button type="submit" size="lg" disabled={isSubmitting} className="w-full md:w-auto min-w-[280px] text-xl font-bold bg-white text-black hover:bg-slate-100 shadow-xl transition-all duration-300 rounded-sm py-8 uppercase tracking-tight">
          {isSubmitting ? <>
              <Loader className="mr-2 h-6 w-6 animate-spin" />
              Processing...
            </> : "Get Your Cash Offer Today"}
        </Button>
      </div>
    </Form>;
}
export default function IgniteYourGrowth() {
  const [typingIntensity, setTypingIntensity] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setTypingIntensity(prev => Math.max(0, prev - 0.05));
    }, 100);
    return () => clearInterval(interval);
  }, []);
  const handleTyping = () => {
    setTypingIntensity(prev => Math.min(1, prev + 0.2));
  };
  return <section id="ready-to-sell" className="relative w-full min-h-[800px] flex items-center justify-center overflow-hidden bg-primary text-primary-foreground py-20">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(0,0,0,0.2)_0%,_transparent_70%)] z-0" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/20 z-0" />
      
      {}
      <WaveformBackground intensity={typingIntensity} />

      <div className="relative z-10 container mx-auto px-4">
        <div className="text-center mb-12 max-w-4xl mx-auto">
          <h2 className="text-5xl md:text-7xl font-extrabold tracking-tighter mb-6 text-white uppercase italic">
            Ready for a Fair Cash Offer?
          </h2>
          <p className="text-xl md:text-2xl text-white/90 font-medium max-w-2xl mx-auto leading-relaxed">
            No repairs. No commissions. No hidden fees. We buy houses in any condition and cover all closing costs.
          </p>
        </div>

        <FormProvider formId="cash_offer_form_home" sectionName="ready-to-sell" defaultValues={{
        name: "",
        email: "",
        phone: "",
        company: "",
        growth_goal: ""
      }} validationRules={{
        name: {
          required: true
        },
        email: {
          required: true,
          pattern: "email"
        },
        growth_goal: {
          required: true
        }
      }} submitText="Get Your Cash Offer Today" successMessage="Your property details have been received. Our team will review the information and contact you with a fair cash offer within 24 hours.">
          <IgniteForm onTyping={handleTyping} />
        </FormProvider>
      </div>
    </section>;
}